import { brand } from "@/shared/lib/safecheckBrand";
import { hospitalDashboardData } from "../data/hospitalDashboard";
import type { HospitalDashboardApiResponse } from "./types";

export type HospitalDashboardViewData = typeof hospitalDashboardData;

function fmtMoney(value: number, decimals = 2): string {
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function fmtMoneyCompact(value: number): string {
  return `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

export function mapHospitalDashboardResponse(
  response: HospitalDashboardApiResponse,
): HospitalDashboardViewData {
  const stats = response.stats;
  const financials = response.financials;
  const displayDate = new Date().toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const companyFilterOptions: HospitalDashboardViewData["companyFilterOptions"] = [
    { value: "", labelKey: "allCompanies" },
    ...response.top_clients.map((client) => ({
      value: client.company,
      label: client.company,
    })),
  ];

  return {
    header: {
      totalInvoices: stats.total_invoices,
      monthlyRevenue: fmtMoneyCompact(response.monthly_revenue),
      providerBadge: response.hospital_name,
      displayDate,
    },
    indicators: [
      {
        id: "invoices",
        value: stats.total_invoices,
        icon: "file",
        iconClassName: "bg-teal-soft text-teal",
        footerKey: "cumulative",
        footerIcon: "minus",
        footerClassName: "text-muted",
      },
      {
        id: "submitted",
        value: stats.submitted_invoices,
        icon: "upload",
        iconClassName: "bg-yellow-soft text-yellow",
        footerKey: "pending",
        footerIcon: "hourglass",
        footerClassName: "text-pink",
      },
      {
        id: "paid",
        value: stats.paid_invoices,
        icon: "wallet",
        iconClassName: "bg-teal-soft text-green",
        footerKey: "settled",
        footerIcon: "dollar",
        footerClassName: "text-green",
      },
      {
        id: "review",
        value: stats.under_review_invoices,
        icon: "clock",
        iconClassName: "bg-primary-soft text-primary",
        footerKey: "inProgress",
        footerIcon: "activity",
        footerClassName: "text-muted",
      },
      {
        id: "approved",
        value: stats.approved_invoices,
        icon: "check",
        iconClassName: "bg-teal-soft text-green",
        footerKey: "validated",
        footerIcon: "trending",
        footerClassName: "text-green",
      },
      {
        id: "rejected",
        value: stats.rejected_invoices,
        icon: "x",
        iconClassName: "bg-pink-soft text-pink",
        footerKey: "toFix",
        footerIcon: "x",
        footerClassName: "text-pink",
      },
      {
        id: "revenue",
        value: fmtMoneyCompact(response.monthly_revenue),
        icon: "dollar",
        iconClassName: "bg-orange-100 text-orange-500",
        footerKey: "thisMonth",
        footerIcon: "trending",
        footerClassName: "text-green",
      },
    ],
    quickActions: hospitalDashboardData.quickActions,
    invoiceDistribution: [
      { key: "submitted", value: stats.submitted_invoices, color: "#eab308" },
      { key: "review", value: stats.under_review_invoices, color: brand.navyLight },
      { key: "approved", value: stats.approved_invoices, color: brand.limeDark },
      { key: "paid", value: stats.paid_invoices, color: brand.lime },
      { key: "rejected", value: stats.rejected_invoices, color: "#ef4444" },
    ],
    financials: {
      totalBilled: fmtMoney(financials.total_billed),
      paid: fmtMoney(financials.paid),
      pending: fmtMoney(financials.pending),
      validationRate: `${financials.validation_rate}%`,
    },
    topClients: response.top_clients.map((client) => ({
      rank: client.rank,
      company: client.company,
      invoiceCount: client.invoice_count,
      totalAmount: client.total_amount,
    })),
    companyFilterOptions,
    submittedInvoices: response.submitted_invoices.map((invoice) => ({
      id: `h-inv-${invoice.id}`,
      displayId: invoice.id,
      company: invoice.company,
      amount: invoice.amount,
      submittedDate: invoice.submitted_date,
      delayDays: invoice.delay_days,
      urgent: invoice.urgent,
    })),
  };
}
